import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';

/** Mirrors OnboardingCallCreditStatus in
 *  cleanse_api/models/billing/onboarding_call_credit.py.
 *
 *  `withdrawn` is distinct from `rejected` on purpose: the org upgraded, so the
 *  trial-conversion offer stopped applying — that is not "staff said no". */
export type OnboardingCallCreditStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'withdrawn';

/** Mirrors OnboardingCallCreditOut in
 *  cleanse_api/api/billing/onboarding_call_credits.py.
 *
 *  `organization_id` is nullable: a booking made with an address that isn't the
 *  signup email arrives unmatched, and staff attach it by hand. */
export interface OnboardingCallCreditRequest {
  id: string;
  organization_id: string | null;
  user_id: string | null;
  invitee_email: string;
  invitee_name: string | null;
  calendly_invitee_uri: string;
  calendly_event_uri: string | null;
  event_type_name: string | null;
  host_email: string | null;
  scheduled_at: string | null;
  status: OnboardingCallCreditStatus;
  credits_awarded: number;
  reviewed_by: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  created_at: string | null;
}

export interface OnboardingCallCreditApproveResponse {
  request: OnboardingCallCreditRequest;
  new_balance: number;
}

export const onboardingCallCreditsApi = createApi({
  reducerPath: 'onboardingCallCreditsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['OnboardingCallCredit'],
  endpoints: (builder) => ({
    /** This org's latest request, or null. Authoritative source for "has this
     *  org booked?" — unlike Clerk metadata, the user can't write it. */
    getMyOnboardingCallCredit: builder.query<
      OnboardingCallCreditRequest | null,
      void
    >({
      query: () => '/onboarding-call-credits/me',
      providesTags: [{ type: 'OnboardingCallCredit', id: 'MINE' }],
    }),

    // Staff-only (server gates on @cleanlist.ai → 403 otherwise).
    // The server drops rows whose org has since upgraded, so the queue only
    // ever shows rewards that still apply.
    adminListPendingOnboardingCallCredits: builder.query<
      OnboardingCallCreditRequest[],
      { limit?: number; offset?: number } | void
    >({
      query: (args) => ({
        url: '/onboarding-call-credits/pending',
        params: { limit: args?.limit ?? 50, offset: args?.offset ?? 0 },
      }),
      providesTags: [{ type: 'OnboardingCallCredit', id: 'PENDING' }],
    }),

    adminApproveOnboardingCallCredit: builder.mutation<
      OnboardingCallCreditApproveResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/onboarding-call-credits/${id}/approve`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'OnboardingCallCredit', id: 'PENDING' }],
    }),

    adminRejectOnboardingCallCredit: builder.mutation<
      OnboardingCallCreditRequest,
      { id: string; reason?: string }
    >({
      query: ({ id, reason }) => ({
        url: `/onboarding-call-credits/${id}/reject`,
        method: 'POST',
        body: { reason: reason || null },
      }),
      invalidatesTags: [{ type: 'OnboardingCallCredit', id: 'PENDING' }],
    }),

    /** Link an unmatched booking to an org before approving it. */
    adminAttachOnboardingCallOrg: builder.mutation<
      OnboardingCallCreditRequest,
      { id: string; organization_id: string; user_id?: string }
    >({
      query: ({ id, organization_id, user_id }) => ({
        url: `/onboarding-call-credits/${id}/attach-organization`,
        method: 'POST',
        body: { organization_id, user_id: user_id || null },
      }),
      invalidatesTags: [{ type: 'OnboardingCallCredit', id: 'PENDING' }],
    }),
  }),
});

export const {
  useGetMyOnboardingCallCreditQuery,
  useAdminListPendingOnboardingCallCreditsQuery,
  useAdminApproveOnboardingCallCreditMutation,
  useAdminRejectOnboardingCallCreditMutation,
  useAdminAttachOnboardingCallOrgMutation,
} = onboardingCallCreditsApi;
